import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

/** Select-or-type combo: pick an existing option or enter a new free-text value. */
function ComboFreeInput({ value, onChange, options, disabled, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const q = String(value || "").toLowerCase();
  const exact = options.some((o) => String(o.value).toLowerCase() === q);
  const filtered = (!typed || !q || exact)
    ? options
    : options.filter((o) => String(o.label || o.value).toLowerCase().includes(q));
  return (
    <div className="relative">
      <Input
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setTyped(true); setOpen(true); }}
        onFocus={() => { setTyped(false); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="h-8 mt-1 text-xs"
        data-testid={testid}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-44 overflow-auto rounded-md border border-slate-200 bg-white shadow-md"
             data-testid={`${testid}-options`}>
          {filtered.map((o) => (
            <button
              type="button"
              key={o.value}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-100"
              onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); }}
              data-testid={`${testid}-opt-${o.value}`}
            >
              {o.label || o.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Renders one admin-defined custom lead field (§8.3) — extracted from LeadDetail.jsx. */
export default function CustomFieldInput({ field, value, onChange, dropdowns, freeText }) {
  const star = field.required ? <span className="text-rose-500 ml-0.5">*</span> : null;
  const wide = field.type === "textarea";
  const options = ((dropdowns || {})[field.dropdown_key] || []).filter((o) => o.active !== false);
  return (
    <div className={wide ? "col-span-2" : ""} data-testid={`custom-field-${field.key}`}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400">{field.label}{star}</Label>
      {field.type === "textarea" ? (
        <Textarea value={value} rows={2} disabled={field.read_only}
                  onChange={(e) => onChange(e.target.value)} className="mt-1 text-xs resize-none" />
      ) : field.type === "select" && freeText ? (
        <ComboFreeInput
          value={value}
          onChange={onChange}
          options={options}
          disabled={field.read_only}
          placeholder={`Select or type ${field.label.toLowerCase()}`}
          testid={`combo-${field.key}`}
        />
      ) : field.type === "select" ? (
        <Select value={value} disabled={field.read_only} onValueChange={onChange}>
          <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue placeholder={`Select ${field.label.toLowerCase()}`} /></SelectTrigger>
          <SelectContent>
            {options.length
              ? options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label || o.value}</SelectItem>)
              : <SelectItem value="__none__" disabled>No options — add them in Dropdown Catalogue</SelectItem>}
          </SelectContent>
        </Select>
      ) : (
        <Input
          type={field.type === "datetime" ? "datetime-local" : ["date", "number", "email"].includes(field.type) ? field.type : "text"}
          value={value} disabled={field.read_only}
          onChange={(e) => onChange(e.target.value)} className="h-8 mt-1 text-xs" />
      )}
    </div>
  );
}
